import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CheckIcon, CopyIcon } from 'lucide-react';
import {
  Button,
  ConfirmDialog,
  Drawer,
  ErrorNotice,
  Field,
  LoadingState,
  TextArea,
  TextInput,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import {
  createPaymentLink,
  parseRecipients,
  paymentLinkQueryKey,
  readPaymentLink,
  revokePaymentLink,
  sendPaymentLink,
  type PaymentLinkView,
} from '@/lib/payment-links';

/** Most addresses one send can go to (the API's limit). */
const MAX_RECIPIENTS = 5;
/** Loose shape check; the API validates each address properly. */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function when(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
}

export interface PaymentLinkDrawerProps {
  readonly eventId: string;
  /** Names the event in the header: "#9704-BC · Nixon Peabody". */
  readonly title: string;
  readonly open: boolean;
  readonly onClose: () => void;
}

/**
 * An event's no-login payment link: make it, copy it, email it, or revoke it.
 * Anyone holding the link can pay the event's balance by bank or card — often
 * the client's accounts-payable team, who have no portal login.
 *
 * @param props - The event and the drawer's open state.
 * @returns The drawer.
 */
export function PaymentLinkDrawer({ eventId, title, open, onClose }: PaymentLinkDrawerProps): React.ReactElement | null {
  const queryClient = useQueryClient();
  const key = paymentLinkQueryKey(eventId);
  const view = useQuery({ queryKey: key, queryFn: ({ signal }) => readPaymentLink(eventId, signal), enabled: open });
  const [copied, setCopied] = useState(false);
  const [recipients, setRecipients] = useState<string | null>(null);
  const [recipientName, setRecipientName] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  const setLink = (link: PaymentLinkView['link']) =>
    queryClient.setQueryData<PaymentLinkView>(key, (previous) => ({ suggestedRecipient: previous?.suggestedRecipient ?? null, link }));
  const create = useMutation({ mutationFn: () => createPaymentLink(eventId), onSuccess: setLink });
  const send = useMutation({
    // Sends what the form shows, including the pre-filled greeting name.
    mutationFn: (input: { to: string[]; greeting: string }) =>
      sendPaymentLink(eventId, {
        to: input.to,
        ...(input.greeting.trim() ? { recipientName: input.greeting.trim() } : {}),
        ...(message.trim() ? { message: message.trim() } : {}),
      }),
    onSuccess: (link) => {
      setLink(link);
      setMessage('');
      toast.success(`Payment link sent to ${link.lastSentTo.join(', ')}`);
    },
  });
  const revoke = useMutation({
    mutationFn: (linkId: string) => revokePaymentLink(linkId),
    onSuccess: () => {
      setConfirmRevoke(false);
      setLink(null);
      toast.success('Payment link revoked. The old link no longer works.');
    },
  });

  const suggested = view.data?.suggestedRecipient ?? null;
  const toValue = recipients ?? suggested?.email ?? '';
  const nameValue = recipientName ?? suggested?.name ?? '';
  const link = view.data?.link ?? null;

  function submit(event: React.FormEvent): void {
    event.preventDefault();
    const to = parseRecipients(toValue);
    if (to.length === 0 || to.some((address) => !EMAIL.test(address))) {
      setFormError('Enter one or more email addresses, separated by commas.');
      return;
    }
    if (to.length > MAX_RECIPIENTS) {
      setFormError(`Send to at most ${MAX_RECIPIENTS} addresses at a time.`);
      return;
    }
    setFormError(null);
    send.mutate({ to, greeting: nameValue });
  }

  return (
    <Drawer open={open} onClose={onClose} title="Payment link" subtitle={title}>
      {view.isPending ? (
        <LoadingState label="Loading the payment link…" />
      ) : view.isError ? (
        <ErrorNotice title="Payment link unavailable" message={errorMessage(view.error)} />
      ) : (
        <div className="space-y-6">
          <p className="text-portal-small text-portal-muted">
            Anyone with this link can pay this event's balance by bank account or card, with no login. It keeps working until
            the balance is paid or you revoke it.
          </p>

          {link ? (
            <div className="space-y-2">
              <Field label="Link" htmlFor="payment-link-url">
                <div className="flex gap-2">
                  <TextInput id="payment-link-url" readOnly value={link.url} onFocus={(event) => event.target.select()} />
                  <Button
                    variant="secondary"
                    onClick={() => {
                      navigator.clipboard.writeText(link.url).catch(() => {});
                      setCopied(true);
                      window.setTimeout(() => setCopied(false), 1500);
                    }}
                  >
                    {copied ? <CheckIcon className="size-4" aria-hidden /> : <CopyIcon className="size-4" aria-hidden />}
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </div>
              </Field>
              <p className="text-portal-micro text-portal-muted">
                {link.openCount === 0 ? 'Not opened yet' : `Opened ${link.openCount} ${link.openCount === 1 ? 'time' : 'times'}, last ${when(link.lastOpenedAt)}`}
                {link.lastSentAt ? ` · Last sent to ${link.lastSentTo.join(', ')} ${when(link.lastSentAt)}` : ''}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <Button disabled={create.isPending} onClick={() => create.mutate()}>
                {create.isPending ? 'Creating…' : 'Create payment link'}
              </Button>
              {create.isError ? <ErrorNotice inline title="No link created" message={errorMessage(create.error)} /> : null}
            </div>
          )}

          <form className="space-y-4 border-t border-portal-line pt-5" onSubmit={submit} noValidate>
            <h3 className="text-portal-small font-bold text-portal-ink">Email the link</h3>
            <Field label="To" htmlFor="payment-link-to" hint="Separate several addresses with commas." required>
              <TextInput id="payment-link-to" type="text" inputMode="email" autoComplete="email" value={toValue} onChange={(event) => setRecipients(event.target.value)} />
            </Field>
            <Field label="Greeting name" htmlFor="payment-link-name" hint="Starts the email: “Hi Julianna,”. Leave empty for “Hello,”.">
              <TextInput id="payment-link-name" value={nameValue} onChange={(event) => setRecipientName(event.target.value)} />
            </Field>
            <Field label="Message" htmlFor="payment-link-message" hint="Leave empty to use the standard note with the balance due.">
              <TextArea id="payment-link-message" rows={3} value={message} onChange={(event) => setMessage(event.target.value)} />
            </Field>
            {formError ? <ErrorNotice inline title="Check the recipients" message={formError} /> : null}
            {send.isError ? <ErrorNotice inline title="Not sent" message={errorMessage(send.error)} /> : null}
            <Button type="submit" disabled={send.isPending}>
              {send.isPending ? 'Sending…' : link ? 'Send link' : 'Create and send link'}
            </Button>
          </form>

          {link ? (
            <div className="border-t border-portal-line pt-5">
              <Button variant="ghost" onClick={() => setConfirmRevoke(true)}>
                Revoke link
              </Button>
              <ConfirmDialog
                open={confirmRevoke}
                title="Revoke this payment link?"
                message="The link stops working at once, wherever it was sent. You can create a new one afterwards."
                confirmLabel="Revoke link"
                tone="danger"
                pending={revoke.isPending}
                onConfirm={() => revoke.mutate(link.id)}
                onCancel={() => setConfirmRevoke(false)}
              />
            </div>
          ) : null}
        </div>
      )}
    </Drawer>
  );
}
